import { Column, Entity, Index } from 'typeorm';
import type { FormSignatureMode } from '../../forms/form-types';
import { BaseEntity } from './base.entity';

/**
 * PD-13768 — who signed a sent form, when, from where, and a fingerprint of exactly what
 * they were shown. One row for the customer's signature and one for a countersignature.
 *
 * `fingerprint` is the SHA-256 (hex) of the canonical JSON of what was shown: the form
 * version, every value as rendered, and the full terms text (sent-forms.ts
 * `sentFormSignedContent`). Recomputing it from the stored submission shows whether the
 * record still matches what was signed.
 *
 * Kept for as long as the client exists, and after the client is deleted for the
 * retention the form or the tenant sets; the IP address here is never cleared by the
 * web-submission retention sweep (it is part of the signing record, not abuse data).
 */
@Index('idx_form_signing_record_submission', ['fkFormSubmissionId'], {})
@Entity('form_signing_record')
export class FormSigningRecordEntity extends BaseEntity {
	@Column('int', { name: 'fk_form_submission__id', unsigned: true })
	public fkFormSubmissionId!: number;

	@Column('int', { name: 'fk_sent_form__id', nullable: true, unsigned: true })
	public fkSentFormId!: number | null;

	@Column('varchar', { name: 'role', length: 20 })
	public role!: 'customer' | 'countersign';

	@Column('varchar', { name: 'signerName', length: 255 })
	public signerName!: string;

	@Column('varchar', { name: 'signerTitle', nullable: true, length: 255 })
	public signerTitle!: string | null;

	@Column('varchar', { name: 'signerEmail', nullable: true, length: 255 })
	public signerEmail!: string | null;

	// The countersigner (tenant user id); null for the customer.
	@Column('int', { name: 'fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('varchar', { name: 'signatureMode', nullable: true, length: 10 })
	public signatureMode!: FormSignatureMode | null;

	@Column('datetime', { name: 'signedAt' })
	public signedAt!: Date;

	@Column('varchar', { name: 'ipAddress', nullable: true, length: 64 })
	public ipAddress!: string | null;

	@Column('varchar', { name: 'userAgent', nullable: true, length: 512 })
	public userAgent!: string | null;

	@Column('char', { name: 'fingerprint', length: 64 })
	public fingerprint!: string;

	// The terms version (policy version date) of each terms statement accepted.
	@Column('json', { name: 'termsVersions', nullable: true })
	public termsVersions!: Record<string, string> | null;
}
